// Nomenclature Service
// Manages all reference data for the Protocol system

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

// Generic Nomenclature Service
class NomenclatureService {
  constructor(collectionName) {
    this.collectionName = collectionName;
  }

  /**
   * Get collection reference
   */
  getCollection() {
    return collection(db, this.collectionName);
  }

  /**
   * Create new nomenclature item
   */
  async create(data, userId) {
    const now = Timestamp.now();
    
    const itemData = {
      ...data,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    };
    
    const docRef = await addDoc(this.getCollection(), itemData);
    return docRef.id;
  }

  /**
   * Get item by ID
   */
  async getById(id) {
    const docRef = doc(this.getCollection(), id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }

  /**
   * Get all items
   */
  async getAll(activeOnly = false) {
    let q;
    
    if (activeOnly) {
      // For now, get all items and filter in memory to avoid index requirements
      q = query(this.getCollection());
    } else {
      q = query(this.getCollection());
    }
    
    const snapshot = await getDocs(q);
    let items = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter active items in memory if needed
    if (activeOnly) {
      items = items.filter(item => item.isActive !== false);
    }
    
    // Sort by displayOrder in memory
    items.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    
    return items;
  }

  /**
   * Update item
   */
  async update(id, updates, userId) {
    const docRef = doc(this.getCollection(), id);
    
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  /**
   * Delete item (soft delete by setting isActive to false)
   */
  async delete(id, userId) {
    await this.update(id, { isActive: false }, userId);
  }

  /**
   * Hard delete (permanent removal)
   */
  async hardDelete(id) {
    const docRef = doc(this.getCollection(), id);
    await deleteDoc(docRef);
  }

  /**
   * Reorder items
   */
  async reorder(items, userId) {
    const batch = items.map(item => 
      this.update(item.id, { displayOrder: item.displayOrder }, userId)
    );
    await Promise.all(batch);
  }
}

// ==================== SPECIFIC SERVICE INSTANCES ====================

export const protocolCategoryService = new NomenclatureService('protocol_categories');
export const protocolTypeService = new NomenclatureService('protocol_types');
export const roomTypeService = new NomenclatureService('room_types');
export const ppeTypeService = new NomenclatureService('ppe_types');
export const verificationMethodService = new NomenclatureService('verification_methods');
export const triggerEventService = new NomenclatureService('trigger_events');
export const frequencyTypeService = new NomenclatureService('frequency_types');
export const locationTypeService = new NomenclatureService('location_types');
export const actionTypeService = new NomenclatureService('action_types');
export const equipmentService = new NomenclatureService('equipment');
export const supplyService = new NomenclatureService('supplies');
export const conditionTypeService = new NomenclatureService('condition_types');
export const documentationFieldTypeService = new NomenclatureService('documentation_field_types');
export const riskTypeService = new NomenclatureService('risk_types');
export const processCategoryService = new NomenclatureService('process_categories');

// ==================== UNIFIED SERVICE ====================

export class UnifiedNomenclatureService {
  /**
   * Get service by type
   */
  getService(type) {
    const services = {
      protocolCategories: protocolCategoryService,
      protocolTypes: protocolTypeService,
      roomTypes: roomTypeService,
      ppeTypes: ppeTypeService,
      verificationMethods: verificationMethodService,
      triggerEvents: triggerEventService,
      frequencyTypes: frequencyTypeService,
      locationTypes: locationTypeService,
      actionTypes: actionTypeService,
      equipment: equipmentService,
      supplies: supplyService,
      conditionTypes: conditionTypeService,
      documentationFieldTypes: documentationFieldTypeService,
      riskTypes: riskTypeService,
      processCategories: processCategoryService
    };
    
    return services[type];
  }

  /**
   * Initialize default nomenclatures (run once on setup)
   */
  async initializeDefaults(userId) {
    // Protocol Categories
    const categories = [
      { code: 'HYG', name: 'Hygiene', description: 'Hand hygiene and personal hygiene protocols', isActive: true, displayOrder: 1, icon: '🧼', color: '#3B82F6' },
      { code: 'DI', name: 'Device Insertion', description: 'Medical device insertion procedures', isActive: true, displayOrder: 2, icon: '💉', color: '#EF4444' },
      { code: 'DM', name: 'Device Maintenance', description: 'Medical device maintenance and care', isActive: true, displayOrder: 3, icon: '🔧', color: '#F59E0B' },
      { code: 'EC', name: 'Environmental Cleaning', description: 'Environmental cleaning and disinfection', isActive: true, displayOrder: 4, icon: '🧹', color: '#10B981' },
      { code: 'PC', name: 'Patient Care', description: 'General patient care protocols', isActive: true, displayOrder: 5, icon: '🏥', color: '#8B5CF6' },
      { code: 'MA', name: 'Medication Administration', description: 'Medication administration procedures', isActive: true, displayOrder: 6, icon: '💊', color: '#EC4899' },
      { code: 'SRG', name: 'Surgical', description: 'Surgical and perioperative protocols', isActive: true, displayOrder: 7, icon: '⚕️', color: '#6366F1' },
      { code: 'ISO', name: 'Isolation', description: 'Isolation and quarantine protocols', isActive: true, displayOrder: 8, icon: '🚪', color: '#DC2626' },
      { code: 'SC', name: 'Specimen Collection', description: 'Specimen collection procedures', isActive: true, displayOrder: 9, icon: '🧪', color: '#14B8A6' }
    ];

    for (const category of categories) {
      await protocolCategoryService.create(category, userId);
    }

    // Room Types
    const roomTypes = [
      { code: 'ICU', name: 'ICU Room', description: 'Intensive Care Unit', isActive: true, displayOrder: 1, highRisk: true },
      { code: 'OR', name: 'Operating Room', description: 'Surgical operating room', isActive: true, displayOrder: 2, highRisk: true },
      { code: 'PR', name: 'Patient Room', description: 'General patient room', isActive: true, displayOrder: 3 },
      { code: 'ER', name: 'Emergency Room', description: 'Emergency department', isActive: true, displayOrder: 4, highRisk: true },
      { code: 'ISO', name: 'Isolation Room', description: 'Isolation room for infectious patients', isActive: true, displayOrder: 5, requiresIsolation: true, highRisk: true },
      { code: 'LAB', name: 'Laboratory', description: 'Laboratory room', isActive: true, displayOrder: 6 },
      { code: 'EXAM', name: 'Examination Room', description: 'Examination room', isActive: true, displayOrder: 7 }
    ];

    for (const roomType of roomTypes) {
      await roomTypeService.create(roomType, userId);
    }

    // PPE Types
    const ppeTypes = [
      { code: 'GLOVES', name: 'Gloves', description: 'Disposable gloves', isActive: true, displayOrder: 1, category: 'Barrier', requiredForIsolation: true },
      { code: 'GOWN', name: 'Gown', description: 'Protective gown', isActive: true, displayOrder: 2, category: 'Barrier', requiredForIsolation: true },
      { code: 'MASK', name: 'Mask', description: 'Surgical mask', isActive: true, displayOrder: 3, category: 'Respiratory', requiredForIsolation: true },
      { code: 'N95', name: 'N95', description: 'N95 respirator', isActive: true, displayOrder: 4, category: 'Respiratory', requiredForIsolation: true },
      { code: 'EYE', name: 'Eye protection', description: 'Eye protection goggles', isActive: true, displayOrder: 5, category: 'Eye/Face' },
      { code: 'SHIELD', name: 'Face shield', description: 'Face shield', isActive: true, displayOrder: 6, category: 'Eye/Face' }
    ];

    for (const ppeType of ppeTypes) {
      await ppeTypeService.create(ppeType, userId);
    }

    // Verification Methods
    const verificationMethods = [
      { code: 'RFID', name: 'RFID Scan', description: 'RFID badge tap verification', isActive: true, displayOrder: 1, requiresDevice: true, automated: true },
      { code: 'BARCODE', name: 'Barcode Scan', description: 'Barcode scanning', isActive: true, displayOrder: 2, requiresDevice: true, automated: true },
      { code: 'VISUAL', name: 'Visual Confirmation', description: 'Observer visual confirmation', isActive: true, displayOrder: 3, automated: false },
      { code: 'TIME', name: 'Time-based', description: 'Auto-confirm after duration', isActive: true, displayOrder: 4, automated: true },
      { code: 'SENSOR', name: 'Sensor', description: 'IoT sensor verification', isActive: true, displayOrder: 5, requiresDevice: true, automated: true },
      { code: 'PHOTO', name: 'Photo', description: 'Photo evidence', isActive: true, displayOrder: 6 },
      { code: 'SIGNATURE', name: 'Signature', description: 'Digital signature', isActive: true, displayOrder: 7 },
      { code: 'CHECKLIST', name: 'Checklist', description: 'Checklist completion', isActive: true, displayOrder: 8 }
    ];

    for (const method of verificationMethods) {
      await verificationMethodService.create(method, userId);
    }

    // Trigger Events
    const triggerEvents = [
      { code: 'PAT_ADM', name: 'Patient Admission', description: 'When a patient is admitted', isActive: true, displayOrder: 1, category: 'Patient', priority: 'High' },
      { code: 'PAT_TRF', name: 'Patient Transfer', description: 'When a patient is transferred', isActive: true, displayOrder: 2, category: 'Patient', priority: 'Medium' },
      { code: 'DEV_INS', name: 'Device Insertion', description: 'When a medical device is inserted', isActive: true, displayOrder: 3, category: 'Device', priority: 'High' },
      { code: 'DEV_REM', name: 'Device Removal', description: 'When a medical device is removed', isActive: true, displayOrder: 4, category: 'Device', priority: 'Medium' },
      { code: 'PRE_PROC', name: 'Pre-procedure', description: 'Before a procedure', isActive: true, displayOrder: 5, category: 'Procedure', priority: 'High' },
      { code: 'POST_PROC', name: 'Post-procedure', description: 'After a procedure', isActive: true, displayOrder: 6, category: 'Procedure', priority: 'Medium' },
      { code: 'SHIFT_CHG', name: 'Shift Change', description: 'During shift handover', isActive: true, displayOrder: 7, category: 'System', priority: 'Low' },
      { code: 'OUTBREAK', name: 'Outbreak Declared', description: 'When an outbreak is declared', isActive: true, displayOrder: 8, category: 'System', priority: 'High' }
    ];

    for (const event of triggerEvents) {
      await triggerEventService.create(event, userId);
    }

    // Frequency Types
    const frequencyTypes = [
      { code: 'SCHED', name: 'Scheduled', description: 'Protocol runs on a schedule', isActive: true, displayOrder: 1, requiresSchedule: true, requiresTrigger: false },
      { code: 'TRIG', name: 'Triggered', description: 'Protocol runs when triggered by event', isActive: true, displayOrder: 2, requiresSchedule: false, requiresTrigger: true },
      { code: 'CONT', name: 'Continuous', description: 'Protocol runs continuously', isActive: true, displayOrder: 3, requiresSchedule: false, requiresTrigger: false },
      { code: 'ONCE', name: 'Once', description: 'Protocol runs once', isActive: true, displayOrder: 4, requiresSchedule: false, requiresTrigger: false }
    ];

    for (const frequencyType of frequencyTypes) {
      await frequencyTypeService.create(frequencyType, userId);
    }

    // Location Types
    const locationTypes = [
      { code: 'DEPT', name: 'Department', description: 'Department level', isActive: true, displayOrder: 1, level: 1 },
      { code: 'ROOM', name: 'Room', description: 'Room level', isActive: true, displayOrder: 2, level: 2 },
      { code: 'BED', name: 'Bed', description: 'Bed level', isActive: true, displayOrder: 3, level: 3 },
      { code: 'PAT', name: 'Patient', description: 'Patient level', isActive: true, displayOrder: 4, level: 4 }
    ];

    for (const locationType of locationTypes) {
      await locationTypeService.create(locationType, userId);
    }

    // Action Types
    const actionTypes = [
      { code: 'WASH', name: 'Wash/Clean', description: 'Washing or cleaning action', isActive: true, displayOrder: 1, icon: 'droplet', category: 'Cleaning' },
      { code: 'DISINFECT', name: 'Disinfect', description: 'Disinfection action', isActive: true, displayOrder: 2, icon: 'spray-can', category: 'Cleaning' },
      { code: 'INSPECT', name: 'Inspect', description: 'Visual inspection action', isActive: true, displayOrder: 3, icon: 'search', category: 'Verification' },
      { code: 'APPLY', name: 'Apply', description: 'Apply substance or equipment', isActive: true, displayOrder: 4, icon: 'hand', category: 'Application' },
      { code: 'REMOVE', name: 'Remove', description: 'Remove item or substance', isActive: true, displayOrder: 5, icon: 'trash', category: 'Removal' },
      { code: 'VERIFY', name: 'Verify', description: 'Verification action', isActive: true, displayOrder: 6, icon: 'check-circle', category: 'Verification' },
      { code: 'DOCUMENT', name: 'Document', description: 'Documentation action', isActive: true, displayOrder: 7, icon: 'file-text', category: 'Documentation' },
      { code: 'WAIT', name: 'Wait/Dwell', description: 'Waiting or dwell time action', isActive: true, displayOrder: 8, icon: 'clock', category: 'Time' }
    ];

    for (const actionType of actionTypes) {
      await actionTypeService.create(actionType, userId);
    }

    // Equipment
    const equipment = [
      { code: 'VENT', name: 'Ventilator', description: 'Mechanical ventilation equipment', isActive: true, displayOrder: 1, category: 'Life Support' },
      { code: 'IV_PUMP', name: 'IV Pump', description: 'Intravenous infusion pump', isActive: true, displayOrder: 2, category: 'Medication' },
      { code: 'MONITOR', name: 'Monitoring Equipment', description: 'Patient monitoring devices', isActive: true, displayOrder: 3, category: 'Monitoring' },
      { code: 'SURG_INST', name: 'Surgical Instruments', description: 'Surgical tools and instruments', isActive: true, displayOrder: 4, category: 'Surgical' },
      { code: 'DIAG', name: 'Diagnostic Equipment', description: 'Diagnostic devices', isActive: true, displayOrder: 5, category: 'Diagnostic' },
      { code: 'LIFE_SUP', name: 'Life Support', description: 'Life support equipment', isActive: true, displayOrder: 6, category: 'Life Support' },
      { code: 'CLEAN', name: 'Cleaning Equipment', description: 'Environmental cleaning tools', isActive: true, displayOrder: 7, category: 'Environmental' },
      { code: 'STER', name: 'Sterilization Equipment', description: 'Sterilization and disinfection equipment', isActive: true, displayOrder: 8, category: 'Environmental' },
      { code: 'MOBILITY', name: 'Mobility Aids', description: 'Wheelchairs, walkers, etc.', isActive: true, displayOrder: 9, category: 'Support' },
      { code: 'LAB_EQ', name: 'Laboratory Equipment', description: 'Lab testing equipment', isActive: true, displayOrder: 10, category: 'Diagnostic' },
      // Romanian equipment list (name = description; categories from Cleaning/Medical/Sanitary/Storage/Computer)
      { code: 'APARAT_NEBBULIZARE', name: 'Aparat de nebulizare', description: 'Aparat de nebulizare', isActive: true, displayOrder: 11, category: 'Medical Equipment' },
      { code: 'BORCANE_ASPIRATIE', name: 'Borcane de aspirație sterile', description: 'Borcane de aspirație sterile', isActive: true, displayOrder: 12, category: 'Medical Equipment' },
      { code: 'CABINE_DUS', name: 'Cabine de duș', description: 'Cabine de duș', isActive: true, displayOrder: 13, category: 'Sanitary Fixtures' },
      { code: 'CHIUVETE', name: 'Chiuvete (inox sau porțelan)', description: 'Chiuvete (inox sau porțelan)', isActive: true, displayOrder: 14, category: 'General' },
      { code: 'COMPUTER', name: 'Computer', description: 'Computer', isActive: true, displayOrder: 15, category: 'Computer Equipment' },
      { code: 'COS_GUNOI_PEDALA', name: 'Coș de gunoi cu pedală și capac', description: 'Coș de gunoi cu pedală și capac', isActive: true, displayOrder: 16, category: 'Cleaning Equipment' },
      { code: 'COSURI_PLASTIC', name: 'Coșuri de plastic', description: 'Coșuri de plastic', isActive: true, displayOrder: 17, category: 'Cleaning Equipment' },
      { code: 'COSURI_TRANSPORT', name: 'Coșuri transport (plastic/metalice)', description: 'Coșuri transport (plastic/metalice)', isActive: true, displayOrder: 18, category: 'Storage & Transport' },
      { code: 'CARUCIOARE_TRANSPORT', name: 'Cărucioare transport (inox)', description: 'Cărucioare transport (inox)', isActive: true, displayOrder: 19, category: 'Storage & Transport' },
      { code: 'CARUCIOR_CURATENIE', name: 'Cărucior de curățenie', description: 'Cărucior de curățenie', isActive: true, displayOrder: 20, category: 'Cleaning Equipment' },
      { code: 'CAZI_BAIE', name: 'Căzi de baie', description: 'Căzi de baie', isActive: true, displayOrder: 21, category: 'General' },
      { code: 'DISPENSERE_PROSOAPE', name: 'Dispensere pentru prosoape de hârtie', description: 'Dispensere pentru prosoape de hârtie', isActive: true, displayOrder: 22, category: 'Sanitary Fixtures' },
      { code: 'DISPENSERE_SAPUN', name: 'Dispensere pentru săpun lichid', description: 'Dispensere pentru săpun lichid', isActive: true, displayOrder: 23, category: 'Sanitary Fixtures' },
      { code: 'FRIGIDERE_TERMOLABILE', name: 'Frigidere pentru materiale termolabile', description: 'Frigidere pentru materiale termolabile', isActive: true, displayOrder: 24, category: 'Storage & Transport' },
      { code: 'GALEATA_10L', name: 'Găleată 10 litri (pentru pavimente)', description: 'Găleată 10 litri (pentru pavimente)', isActive: true, displayOrder: 25, category: 'Cleaning Equipment' },
      { code: 'GALEATA_3_4L', name: 'Găleată 3-4 litri (pentru mobilier)', description: 'Găleată 3-4 litri (pentru mobilier)', isActive: true, displayOrder: 26, category: 'Cleaning Equipment' },
      { code: 'GALEATA_SPALARE', name: 'Găleată pentru spălare/dezinfecție', description: 'Găleată pentru spălare/dezinfecție', isActive: true, displayOrder: 27, category: 'Cleaning Equipment' },
      { code: 'INSTALATIE_ASPIRATIE', name: 'Instalație de aspirație', description: 'Instalație de aspirație', isActive: true, displayOrder: 28, category: 'Medical Equipment' },
      { code: 'LAMPI_UV', name: 'Lămpi UV', description: 'Lămpi UV', isActive: true, displayOrder: 29, category: 'General' },
      { code: 'MASA_GINECOLOGICA', name: 'Masă ginecologică', description: 'Masă ginecologică', isActive: true, displayOrder: 30, category: 'Medical Equipment' },
      { code: 'MOP_PLAT', name: 'Mop plat dedicat', description: 'Mop plat dedicat', isActive: true, displayOrder: 31, category: 'Cleaning Equipment' },
      { code: 'MOUSE', name: 'Mouse', description: 'Mouse', isActive: true, displayOrder: 32, category: 'Computer Equipment' },
      { code: 'OGLINZI', name: 'Oglinzi', description: 'Oglinzi', isActive: true, displayOrder: 33, category: 'General' },
      { code: 'PAT_PACIENT', name: 'Pat pacient', description: 'Pat pacient', isActive: true, displayOrder: 34, category: 'Medical Equipment' },
      { code: 'PERIE_SIFOANE', name: 'Perie pentru sifoane', description: 'Perie pentru sifoane', isActive: true, displayOrder: 35, category: 'Cleaning Equipment' },
      { code: 'PERIE_SUBTIRE', name: 'Perie subțire (pentru interiorul robinetului)', description: 'Perie subțire (pentru interiorul robinetului)', isActive: true, displayOrder: 36, category: 'Cleaning Equipment' },
      { code: 'PERII_WC', name: 'Perii pentru WC', description: 'Perii pentru WC', isActive: true, displayOrder: 37, category: 'Sanitary Fixtures' },
      { code: 'PUBELE_DESEURI', name: 'Pubele pentru deșeuri', description: 'Pubele pentru deșeuri', isActive: true, displayOrder: 38, category: 'Storage & Transport' },
      { code: 'RECIPIENTE_MENAJERE', name: 'Recipiente deșeuri menajere', description: 'Recipiente deșeuri menajere', isActive: true, displayOrder: 39, category: 'Storage & Transport' },
      { code: 'RECIPIENTE_PERICULOASE', name: 'Recipiente deșeuri periculoase', description: 'Recipiente deșeuri periculoase', isActive: true, displayOrder: 40, category: 'Storage & Transport' },
      { code: 'REFLECTOR_SCIALITIC', name: 'Reflector/lampă scialitică', description: 'Reflector/lampă scialitică', isActive: true, displayOrder: 41, category: 'Medical Equipment' },
      { code: 'STATIV_PERFUZIE', name: 'Stativ perfuzie', description: 'Stativ perfuzie', isActive: true, displayOrder: 42, category: 'Medical Equipment' },
      { code: 'TASTATURA', name: 'Tastatura', description: 'Tastatura', isActive: true, displayOrder: 43, category: 'Computer Equipment' },
      { code: 'TERMOHIGROMETRU', name: 'Termohigrometru (calibrat anual)', description: 'Termohigrometru (calibrat anual)', isActive: true, displayOrder: 44, category: 'Storage & Transport' },
      { code: 'VASE_WC', name: 'Vase WC', description: 'Vase WC', isActive: true, displayOrder: 45, category: 'Sanitary Fixtures' }
    ];

    for (const item of equipment) {
      await equipmentService.create(item, userId);
    }

    // Supplies
    const supplies = [
      { code: 'GLOVES', name: 'Gloves', description: 'Disposable gloves', isActive: true, displayOrder: 1, category: 'PPE' },
      { code: 'SYRINGE', name: 'Syringe', description: 'Medical syringe', isActive: true, displayOrder: 2, category: 'Medication' },
      { code: 'GAUZE', name: 'Gauze', description: 'Sterile gauze pads', isActive: true, displayOrder: 3, category: 'Wound Care' },
      { code: 'ALCOHOL', name: 'Alcohol Swabs', description: 'Alcohol prep pads', isActive: true, displayOrder: 4, category: 'Disinfection' },
      { code: 'BANDAGE', name: 'Bandage', description: 'Medical bandages', isActive: true, displayOrder: 5, category: 'Wound Care' },
      { code: 'CATHETER', name: 'Catheter', description: 'Medical catheter', isActive: true, displayOrder: 6, category: 'Medical Device' },
      { code: 'TUBING', name: 'IV Tubing', description: 'Intravenous tubing', isActive: true, displayOrder: 7, category: 'Medication' },
      { code: 'MASK', name: 'Surgical Mask', description: 'Disposable surgical mask', isActive: true, displayOrder: 8, category: 'PPE' },
      { code: 'GOWN', name: 'Isolation Gown', description: 'Disposable isolation gown', isActive: true, displayOrder: 9, category: 'PPE' },
      { code: 'DISINFECT', name: 'Disinfectant', description: 'Surface disinfectant', isActive: true, displayOrder: 10, category: 'Environmental' },
      // Romanian supplies list (codes from list; name = description)
      { code: '1', name: 'Apă', description: 'Apă', isActive: true, displayOrder: 100, category: 'Supplies' },
      { code: '2', name: 'Apă curentă', description: 'Apă curentă', isActive: true, displayOrder: 101, category: 'Supplies' },
      { code: '3', name: 'Apă fierbinte', description: 'Apă fierbinte', isActive: true, displayOrder: 102, category: 'Supplies' },
      { code: '4', name: 'Detergenți', description: 'Detergenți', isActive: true, displayOrder: 103, category: 'Supplies' },
      { code: '5', name: 'Dezinfectant Tip 2 (pentru spații medicale și rezerve)', description: 'Dezinfectant Tip 2 (pentru spații medicale și rezerve)', isActive: true, displayOrder: 104, category: 'Supplies' },
      { code: '6', name: 'Dezinfectant Tip 4 (pentru oficii alimentare/biberonerie)', description: 'Dezinfectant Tip 4 (pentru oficii alimentare/biberonerie)', isActive: true, displayOrder: 105, category: 'Supplies' },
      { code: '8', name: 'Dezinfectant de nivel scăzut', description: 'Dezinfectant de nivel scăzut', isActive: true, displayOrder: 106, category: 'Supplies' },
      { code: '9', name: 'Dezinfectant de nivel înalt', description: 'Dezinfectant de nivel înalt', isActive: true, displayOrder: 107, category: 'Supplies' },
      { code: '10', name: 'Grafic de evidență a curățeniei și dezinfecției', description: 'Grafic de evidență a curățeniei și dezinfecției', isActive: true, displayOrder: 108, category: 'Supplies' },
      { code: '11', name: 'Grafic de temperatură frigidere', description: 'Grafic de temperatură frigidere', isActive: true, displayOrder: 109, category: 'Supplies' },
      { code: '12', name: 'Halat de protecție', description: 'Halat de protecție', isActive: true, displayOrder: 110, category: 'Supplies' },
      { code: '13', name: 'Instrument de scris', description: 'Instrument de scris', isActive: true, displayOrder: 111, category: 'Supplies' },
      { code: '18', name: 'Lavete curate și dezinfectate (generale)', description: 'Lavete curate și dezinfectate (generale)', isActive: true, displayOrder: 112, category: 'Supplies' },
      { code: '19', name: 'Lavete pentru electronice', description: 'Lavete pentru electronice', isActive: true, displayOrder: 113, category: 'Supplies' },
      { code: '20', name: 'Lavete pentru oglinzi', description: 'Lavete pentru oglinzi', isActive: true, displayOrder: 114, category: 'Supplies' },
      { code: '21', name: 'Lavete uscate', description: 'Lavete uscate', isActive: true, displayOrder: 115, category: 'Supplies' },
      { code: '22', name: 'Loțiune pentru îngrijirea mâinilor (antiseptic)', description: 'Loțiune pentru îngrijirea mâinilor (antiseptic)', isActive: true, displayOrder: 116, category: 'Supplies' },
      { code: '23', name: 'Mască de protecție', description: 'Mască de protecție', isActive: true, displayOrder: 117, category: 'Supplies' },
      { code: '24', name: 'Material steril (general)', description: 'Material steril (general)', isActive: true, displayOrder: 118, category: 'Supplies' },
      { code: '25', name: 'Mănuși de latex nesterile', description: 'Mănuși de latex nesterile', isActive: true, displayOrder: 119, category: 'Supplies' },
      { code: '27', name: 'Mănuși menajere', description: 'Mănuși menajere', isActive: true, displayOrder: 120, category: 'Supplies' },
      { code: '28', name: 'Măști oxigen sterile', description: 'Măști oxigen sterile', isActive: true, displayOrder: 121, category: 'Supplies' },
      { code: '29', name: 'Ochelari de protecție', description: 'Ochelari de protecție', isActive: true, displayOrder: 122, category: 'Supplies' },
      { code: '30', name: 'Olițe', description: 'Olițe', isActive: true, displayOrder: 123, category: 'Supplies' },
      { code: '32', name: 'Paduri GALBENE pentru mop', description: 'Paduri GALBENE pentru mop', isActive: true, displayOrder: 124, category: 'Supplies' },
      { code: '33', name: 'Paduri ROȘII pentru mop', description: 'Paduri ROȘII pentru mop', isActive: true, displayOrder: 125, category: 'Supplies' },
      { code: '34', name: 'Paduri VERZI pentru mop', description: 'Paduri VERZI pentru mop', isActive: true, displayOrder: 126, category: 'Supplies' },
      { code: '35', name: 'Paduri preumezite', description: 'Paduri preumezite', isActive: true, displayOrder: 127, category: 'Supplies' },
      { code: '36', name: 'Ploști', description: 'Ploști', isActive: true, displayOrder: 128, category: 'Supplies' },
      { code: '38', name: 'Produse de curățat (generale)', description: 'Produse de curățat (generale)', isActive: true, displayOrder: 129, category: 'Supplies' },
      { code: '41', name: 'Prosoape de hârtie', description: 'Prosoape de hârtie', isActive: true, displayOrder: 130, category: 'Supplies' },
      { code: '42', name: 'Protectoare faciale', description: 'Protectoare faciale', isActive: true, displayOrder: 131, category: 'Supplies' },
      { code: '43', name: 'Registru de evidență a utilizării lămpilor UV', description: 'Registru de evidență a utilizării lămpilor UV', isActive: true, displayOrder: 132, category: 'Supplies' },
      { code: '45', name: 'Saci pentru depozitare', description: 'Saci pentru depozitare', isActive: true, displayOrder: 133, category: 'Supplies' },
      { code: '48', name: 'Soluție detartrant', description: 'Soluție detartrant', isActive: true, displayOrder: 134, category: 'Supplies' },
      { code: '49', name: 'Soluție detergent-dezinfectant (Biocid Grupa Principală 1, Tip 2)', description: 'Soluție detergent-dezinfectant (Biocid Grupa Principală 1, Tip 2)', isActive: true, displayOrder: 135, category: 'Supplies' },
      { code: '50', name: 'Soluție pentru curățat oglinzi', description: 'Soluție pentru curățat oglinzi', isActive: true, displayOrder: 136, category: 'Supplies' },
      { code: '51', name: 'Soluție pentru electronice', description: 'Soluție pentru electronice', isActive: true, displayOrder: 137, category: 'Supplies' },
      { code: '52', name: 'Săpun lichid', description: 'Săpun lichid', isActive: true, displayOrder: 138, category: 'Supplies' },
      { code: '53', name: 'Tacâmuri', description: 'Tacâmuri', isActive: true, displayOrder: 139, category: 'Supplies' },
      { code: '54', name: 'Tubulatura sterilă', description: 'Tubulatura sterilă', isActive: true, displayOrder: 140, category: 'Supplies' },
      { code: '55', name: 'Urinare', description: 'Urinare', isActive: true, displayOrder: 141, category: 'Supplies' },
      { code: '56', name: 'Ustensile de consum', description: 'Ustensile de consum', isActive: true, displayOrder: 142, category: 'Supplies' },
      { code: '57', name: 'Veselă', description: 'Veselă', isActive: true, displayOrder: 143, category: 'Supplies' }
    ];

    for (const item of supplies) {
      await supplyService.create(item, userId);
    }

    // Condition Types
    const conditionTypes = [
      { code: 'PREREQ', name: 'Prerequisite', description: 'Condition that must be met before action can proceed', isActive: true, displayOrder: 1, category: 'Prerequisite' },
      { code: 'SAFETY', name: 'Safety', description: 'Safety-related condition', isActive: true, displayOrder: 2, category: 'Safety' },
      { code: 'WARNING', name: 'Warning', description: 'Warning condition that should be considered', isActive: true, displayOrder: 3, category: 'Warning' },
      { code: 'ENV', name: 'Environmental', description: 'Environmental condition', isActive: true, displayOrder: 4, category: 'Environmental' },
      { code: 'PATIENT', name: 'Patient Condition', description: 'Patient-specific condition', isActive: true, displayOrder: 5, category: 'Patient' },
      { code: 'EQUIP', name: 'Equipment Availability', description: 'Equipment must be available', isActive: true, displayOrder: 6, category: 'Resource' },
      { code: 'SUPPLY', name: 'Supply Availability', description: 'Supply must be available', isActive: true, displayOrder: 7, category: 'Resource' },
      { code: 'GALBEN', name: 'GALBEN', description: 'Săli de tratament (chiuveta, dulap, oglinda, gresia, faianta)', isActive: true, displayOrder: 8, category: 'Color code', color: '#FACC15' },
      { code: 'ALBASTRU', name: 'ALBASTRU', description: 'Suprafețe generale cu risc scăzut (saloane pacienți, holuri, geamuri, pardoseli)', isActive: true, displayOrder: 9, category: 'Color code', color: '#3B82F6' },
      { code: 'ROSU', name: 'ROSU', description: 'Risc foarte mare de contaminare (WC)', isActive: true, displayOrder: 10, category: 'Color code', color: '#DC2626' }
    ];

    for (const conditionType of conditionTypes) {
      await conditionTypeService.create(conditionType, userId);
    }

    // Documentation Field Types
    const documentationFieldTypes = [
      { code: 'TEXT', name: 'Text', description: 'Text input field', isActive: true, displayOrder: 1, category: 'Input' },
      { code: 'NUMBER', name: 'Number', description: 'Numeric input field', isActive: true, displayOrder: 2, category: 'Input' },
      { code: 'BOOLEAN', name: 'Boolean', description: 'Yes/No or True/False field', isActive: true, displayOrder: 3, category: 'Input' },
      { code: 'SELECT', name: 'Select', description: 'Dropdown selection field', isActive: true, displayOrder: 4, category: 'Input' },
      { code: 'DATETIME', name: 'Date/Time', description: 'Date and time field', isActive: true, displayOrder: 5, category: 'Input' },
      { code: 'DATE', name: 'Date', description: 'Date only field', isActive: true, displayOrder: 6, category: 'Input' },
      { code: 'TIME', name: 'Time', description: 'Time only field', isActive: true, displayOrder: 7, category: 'Input' },
      { code: 'TEXTAREA', name: 'Text Area', description: 'Multi-line text field', isActive: true, displayOrder: 8, category: 'Input' }
    ];

    for (const fieldType of documentationFieldTypes) {
      await documentationFieldTypeService.create(fieldType, userId);
    }

    // Risk Types
    const riskTypes = [
      { code: 'INF_TRANS', name: 'Infection Transmission', description: 'Risk of infection transmission between patients, staff, or environment', isActive: true, displayOrder: 1, category: 'Infection' },
      { code: 'DEV_CONT', name: 'Device Contamination', description: 'Risk of contamination of medical devices or equipment', isActive: true, displayOrder: 2, category: 'Device' },
      { code: 'ENV_CONT', name: 'Environmental Contamination', description: 'Risk of environmental contamination in healthcare facilities', isActive: true, displayOrder: 3, category: 'Environmental' },
      { code: 'CROSS_CONT', name: 'Cross-contamination', description: 'Risk of cross-contamination between different areas or processes', isActive: true, displayOrder: 4, category: 'Infection' },
      { code: 'MED_ERR', name: 'Medication Error', description: 'Risk of medication administration errors', isActive: true, displayOrder: 5, category: 'Medication' },
      { code: 'PAT_HARM', name: 'Patient Harm', description: 'Risk of harm to patient safety or well-being', isActive: true, displayOrder: 6, category: 'Patient Safety' },
      { code: 'STAFF_EXP', name: 'Staff Exposure', description: 'Risk of healthcare worker exposure to pathogens or hazards', isActive: true, displayOrder: 7, category: 'Occupational Safety' }
    ];

    for (const riskType of riskTypes) {
      await riskTypeService.create(riskType, userId);
    }

    // Protocol Types (if not already initialized)
    const existingProtocolTypes = await protocolTypeService.getAll();
    if (existingProtocolTypes.length === 0) {
      const protocolTypes = [
        { code: 'HYGIENE', name: 'Hygiene Protocol', description: 'Hand hygiene and sanitation', isActive: true, displayOrder: 1, category: 'HYG' },
        { code: 'CLEANING', name: 'Cleaning Protocol', description: 'Environmental cleaning', isActive: true, displayOrder: 2, category: 'EC' },
        { code: 'ISOLATION', name: 'Isolation Protocol', description: 'Patient isolation procedures', isActive: true, displayOrder: 3, category: 'ISO' },
        { code: 'DEVICE', name: 'Device Protocol', description: 'Medical device handling', isActive: true, displayOrder: 4, category: 'DM' },
        { code: 'PROCEDURE', name: 'Procedure Protocol', description: 'Clinical procedures', isActive: true, displayOrder: 5, category: 'PC' },
        { code: 'PPE', name: 'PPE Protocol', description: 'Personal protective equipment', isActive: true, displayOrder: 6, category: 'PC' },
        { code: 'WASTE', name: 'Waste Management', description: 'Waste disposal procedures', isActive: true, displayOrder: 7, category: 'EC' },
        { code: 'MEDICATION', name: 'Medication Protocol', description: 'Medication administration', isActive: true, displayOrder: 8, category: 'MA' }
      ];

      for (const protocolType of protocolTypes) {
        await protocolTypeService.create(protocolType, userId);
      }
    }

    console.log('✅ Default nomenclatures initialized successfully');
  }
}

export const unifiedNomenclatureService = new UnifiedNomenclatureService();

